import { Stack } from '@startuptools/ui/Stack';
import { GqlMeeting } from '../../../../../graphql/base-types.graphql';
import { Trans } from '@lingui/react';
import { GqlMeetingParticipant } from '../../../../../graphql/base-types.graphql';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { useMemo } from 'react';
import { orderBy } from 'lodash-es';
import { useShmFunctionSelectSuspenseQuery } from './FunctionSelect.graphql';
import { FunctionSelect } from './FunctionSelectDialog';
import { Divider } from '@startuptools/ui/Divider';
import { Alert } from '@startuptools/ui/Alert';
import { FunctionKind } from '../types';

export const AiShmFunctionSelect = ({
  meeting,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'companyId'> & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
}) => {
  const {
    data: { companyDirectors },
  } = useShmFunctionSelectSuspenseQuery({ variables: { companyId: meeting.companyId } });

  const { chairman, secretary, adjusters } = useMemo(() => {
    const chairman = meeting.meetingParticipants.find(mp => mp.chairman);
    const secretary = meeting.meetingParticipants.find(mp => mp.secretary);
    const adjusters = orderBy(
      meeting.meetingParticipants.filter(mp => mp.adjuster),
      mp => mp.identity.name,
    );
    return { chairman, secretary, adjusters };
  }, [meeting.meetingParticipants]);

  const errorChairman = useMemo(
    () => !meeting.meetingParticipants.some(mp => mp.chairman),
    [meeting.meetingParticipants],
  );
  const errorAdjusters = useMemo(
    () =>
      companyDirectors.length >= 1 &&
      meeting.meetingParticipants.length > 1 &&
      !meeting.meetingParticipants.some(mp => mp.adjuster),
    [meeting.meetingParticipants, companyDirectors.length],
  );

  return (
    <>
      <Stack gap={3}>
        <div /* important div to prevent component from crashing when the value is updated from API response */>
          <FunctionSelect
            title={<Trans id="Chairman" />}
            placeholder={<Trans id="Add Chairman" />}
            kind={FunctionKind.Chairman}
            meeting={meeting}
            value={chairman?.id}
            key={chairman?.id}
          />
        </div>

        <Divider />

        <div /* important div to prevent component from crashing when the value is updated from API response */>
          <FunctionSelect
            title={<Trans id="Adjusters" />}
            placeholder={<Trans id="Add Adjuster" />}
            kind={FunctionKind.Adjuster}
            meeting={meeting}
            multiple
            value={adjusters.map(mp => mp.id)}
            key={adjusters.map(mp => mp.id).join(',')}
          />
        </div>

        <Divider />

        <div /* important div to prevent component from crashing when the value is updated from API response */>
          <FunctionSelect
            title={<Trans id="Secretary" />}
            placeholder={<Trans id="Add Secretary" />}
            kind={FunctionKind.Secretary}
            meeting={meeting}
            value={secretary?.id}
            key={secretary?.id}
          />
        </div>

        {errorChairman && (
          <Alert color="danger">
            <Trans id="You have to pick a chairman" />
          </Alert>
        )}
        {errorAdjusters && (
          <Alert color="danger">
            <Trans id="You have to pick at least one verifier" />
          </Alert>
        )}
      </Stack>
    </>
  );
};
